import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";

import type { DashboardTable } from "./helpers";

type CustomTableContextValue = {
  containerRef: React.RefObject<HTMLDivElement | null>;
  unfilledWidth: number;
};

const CustomTableContext = createContext<CustomTableContextValue | null>(null);

type CustomTableProviderProps<TColumnData extends {}> = {
  table: DashboardTable<TColumnData>;
  children: React.ReactNode;
};

export function CustomTableProvider<TColumnData extends {}>({
  table,
  children,
}: CustomTableProviderProps<TColumnData>): React.ReactNode {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [widthContainer, setWidthContainer] = useState(0);

  useEffect(() => {
    const updateWidth = (entries: ResizeObserverEntry[]) => {
      const width = entries.at(-1)?.contentRect.width;
      if (width !== undefined) {
        setWidthContainer(width);
      }
    };
    const documentObserver = new ResizeObserver(updateWidth);
    const containerObserver = new ResizeObserver(updateWidth);
    const container = containerRef.current;

    documentObserver.observe(document.body);
    if (container) {
      containerObserver.observe(container);
    }

    return () => {
      documentObserver.disconnect();
      containerObserver.disconnect();
    };
  }, []);

  const widthTableContent = table.getTotalSize();
  const unfilledWidth = Math.max(0, widthContainer - widthTableContent);

  const contextValue = useMemo(() => ({ containerRef, unfilledWidth }), [containerRef, unfilledWidth]);

  return <CustomTableContext value={contextValue}>{children}</CustomTableContext>;
}

export function useCustomTable(): CustomTableContextValue {
  const context = useContext(CustomTableContext);

  if (!context) {
    throw new Error("useCustomTable must be used within a CustomTableProvider");
  }

  return context;
}
